import { useEffect, useRef, useState, type ReactNode } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { readBrowserValue, writeBrowserValue } from './browserStorage'
import { roomRequest } from './roomApi'
import { resumeRoute } from './resumeRoute.mjs'
import type { RoomUser } from './types'

export function ResumeSession({ user, children }: { user: RoomUser; children: ReactNode }) {
  const location = useLocation(), navigate = useNavigate(), [ready, setReady] = useState(false)
  const restored = useRef(false), saved = useRef('')
  const key = 'bramble:last-view:' + user.id
  useEffect(() => {
    if (restored.current) return
    restored.current = true
    const route = resumeRoute(readBrowserValue(key)) || resumeRoute(user.preferences.lastView)
    if (user.preferences.resumeLastView && location.pathname === '/' && !location.search && route) {
      saved.current = route
      void navigate(route, { replace: true })
    }
    setReady(true)
  }, [])
  useEffect(() => {
    if (!ready || !user.preferences.resumeLastView) return
    const route = resumeRoute(location.pathname + location.search)
    if (!route || new URLSearchParams(location.search).get('room') === 'profile') return
    writeBrowserValue(key, route)
    if (saved.current === route) return
    const timer = window.setTimeout(() => {
      void roomRequest(user.id, '/api/me/last-view', 'PATCH', { route }).then(() => { saved.current = route }).catch(() => {
        // The local address is still available when the network comes back.
      })
    }, 800)
    return () => window.clearTimeout(timer)
  }, [ready, location.pathname, location.search, user.preferences.resumeLastView, user.id])
  return ready ? children : <main className="app-loading">Opening your writing room…</main>
}
